import { FullArrowLight as FullIcon, ReduceLight as MinimizeIcon, EditLightBig as EditIcon } from '@repo/go-icons';
import { Button, LoaderIcon, Tooltip } from '@repo/go-ui';
import { AlertsManagerWidget as AlertsManagerWidgetComponent } from '@repo/go-widgets';
import { observer } from 'mobx-react-lite';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WIDGET_I18N_NAMESPACES } from 'src/app/i18n/widgetNamespaces';
import { useService } from 'src/packages/di';
import { IS_PORTABLE_DEVICE } from 'src/packages/shared/consts/isPortableDevice';

import { NoWellChosen } from '../no-well-chosen';
import { WidgetHeader } from '../widget-header/WidgetHeader';
import type { TWidgetRendererOptions } from '../workspace/services/WidgetRendererService';

import type { AlertsManagerWidgetEntity } from './AlertsManagerWidget.entity';
import styles from './AlertsManagerWidget.module.scss';
import { AlertsManagerWidgetStore } from './AlertsManagerWidget.store';

type WrapperProps = {
  entity: AlertsManagerWidgetEntity;
  options: TWidgetRendererOptions;
};

type Props = {
  store: AlertsManagerWidgetStore;
  entity: AlertsManagerWidgetEntity;
  options: TWidgetRendererOptions;
};

export const AlertsManagerWidgetWrapper = observer(function AlertsManagerWidgetWrapper({
  entity,
  options,
}: WrapperProps) {
  const [store, setStore] = useState<AlertsManagerWidgetStore | null>(null);
  const storeService = useService('widgetStoreService');

  useEffect(() => {
    if (store) return;

    const _store = storeService.getStore<AlertsManagerWidgetEntity, AlertsManagerWidgetStore>(entity);

    if (_store) {
      setStore(_store);
    } else {
      const newStore = new AlertsManagerWidgetStore(entity, options.groupSelectStore, options.wellListEntity);
      storeService.setStore<AlertsManagerWidgetEntity, AlertsManagerWidgetStore>(entity, newStore);
      setStore(newStore);
    }
  }, [setStore, storeService, entity, options.groupSelectStore, store, options.wellListEntity]);

  if (!store) return null;

  return (
    <Suspense
      fallback={
        <div className={styles.alertsManagerWidgetWrapper}>
          <div className={styles.wrapper}>
            <LoaderIcon />
          </div>
        </div>
      }
    >
      <AlertsManagerWidget store={store} entity={entity} options={options} />
    </Suspense>
  );
});

export const AlertsManagerWidget = observer(function AlertsManagerWidget({ entity, options, store }: Props) {
  const { setGroupForAlertsManager, exitFullScreen, isReady, enableEditMode } = store;
  const { t } = useTranslation(['common', WIDGET_I18N_NAMESPACES.alertsManagerWidget]);

  const { wellId, isFullscreen, state, setFullscreen, wellboreName, isEditMode } = entity;

  useEffect(() => store.effect(), [store, store.effect]);

  const directories = useService('directories');
  const wells = directories.getObject('GOdashboard_Well');
  const wellName = wells?.find((item) => item.id === wellId)?.data.name ?? wellId;

  return (
    <div className={styles.alertsManagerWidgetWrapper}>
      <WidgetHeader
        entity={entity}
        onWidgetChangeGroup={setGroupForAlertsManager}
        groupSelectStore={options.groupSelectStore}
        widgetName={entity.title}
        wellName={wellName}
        wellboreName={wellboreName}
        onWidgetDelete={() => {
          options.onWidgetDelete(entity);
        }}
        showCloseButton={!isFullscreen}
        draggable={!isFullscreen}
        renderAdditionalButtons={() => {
          if (!isReady) return null;

          return (
            <>
              {!isEditMode && !IS_PORTABLE_DEVICE && (
                <Tooltip title={t('common:Widget.edit')} delay={400}>
                  <Button
                    onClick={enableEditMode}
                    variant="flat"
                    icon={<EditIcon className={styles.icon} />}
                    className={styles.button}
                  />
                </Tooltip>
              )}
              {!isFullscreen && (
                <Tooltip title={t('common:Widget.fullscreen')} delay={400}>
                  <Button
                    onClick={() => setFullscreen(true)}
                    variant="flat"
                    icon={<FullIcon className={styles.icon} />}
                    className={styles.button}
                  />
                </Tooltip>
              )}
              {isFullscreen && (
                <Tooltip title={t('common:Widget.collapse')} delay={400}>
                  <Button
                    onClick={() => exitFullScreen()}
                    variant="flat"
                    icon={<MinimizeIcon className={styles.icon} />}
                    className={styles.button}
                  />
                </Tooltip>
              )}
            </>
          );
        }}
      />

      {state ? (
        <div className={styles.wrapper}>
          <AlertsManagerWidgetComponent widgetState={state} />
        </div>
      ) : (
        <NoWellChosen />
      )}
    </div>
  );
});
